import { useState } from 'react';

import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Progress } from '@atom/mui';
import {
  CloseIcon,
  ErrorOutlineIcon,
  type IconComponent,
  InfoIcon,
  SearchOffIcon,
  WorkIcon,
} from '@atom/mui/icons';
import { UserDetail } from '@atom/types/user';
import { WorkOrder } from '@atom/types/work';
import userUtilities from '@atom/utilities/userUtilities';

import SidebarMessage from '../shared/SidebarMessage';
import TabNav from '../shared/TabNav';

import UserInfoTab from './UserInfoTab';
import UserWorkTab from './UserWorkTab';

import sidebarClasses from '../shared/sidebars.module.css';
import classes from './user.module.css';

const TABS: { value: string; icon: IconComponent }[] = [
  { value: 'info', icon: InfoIcon },
  { value: 'work', icon: WorkIcon },
];

export type UserSidebarViewProps = {
  user: UserDetail | undefined;
  workOrders: WorkOrder[];
  loading?: boolean;
  error?: boolean;
  onClose: () => void;
};

const UserSidebarView = ({
  user,
  workOrders,
  loading,
  error,
  onClose,
}: UserSidebarViewProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const [, { Link }] = useNavigation();

  if (loading) {
    return (
      <div className={sidebarClasses.spinnerContainer}>
        <Progress />
      </div>
    );
  }

  if (error) {
    return (
      <SidebarMessage
        icon={ErrorOutlineIcon}
        message="Something went wrong loading this user."
        onClose={onClose}
        variant="error"
      />
    );
  }

  if (!user) {
    return (
      <SidebarMessage
        icon={SearchOffIcon}
        message="This user was not found."
        onClose={onClose}
      />
    );
  }

  return (
    <div className={sidebarClasses.container}>
      <div className={sidebarClasses.header}>
        <div className={sidebarClasses.closeRow}>
          <IconButton onClick={onClose}>
            <Icon icon={CloseIcon} />
          </IconButton>
        </div>
        <div className={classes.userProfileRow}>
          <div className={classes.userAvatar}>
            <img
              src={user.photoUrl}
              alt={userUtilities.getUserFullName(user)}
            />
          </div>
          <div className={classes.userMeta}>
            <div className={classes.userName}>
              {userUtilities.getUserFullName(user)}
            </div>
            <div className={classes.userSubtext}>{user.email}</div>
            <div className={classes.userSubtext}>{user.role}</div>
          </div>
        </div>
      </div>
      <div className={sidebarClasses.trayContentContainer}>
        <TabNav tabs={TABS} active={activeTab} onSelect={setActiveTab} />
        {activeTab === 'info' ? (
          <UserInfoTab user={user} />
        ) : (
          <UserWorkTab workOrders={workOrders} />
        )}
      </div>
      <Link to={`/team/${user.id}`}>
        <div className={sidebarClasses.viewDetailButton}>View Full Detail</div>
      </Link>
    </div>
  );
};

export default UserSidebarView;
